// Import React
import React from 'react';

// Import Spectacle Core tags
import {
  BlockQuote,
  Cite,
  Deck,
  Heading,
  ListItem,
  List,
  Link,
  Quote,
  Slide,
  Text,
  Code,
  CodePane,
} from 'spectacle';

// Import theme
import createTheme from 'spectacle/lib/themes/default';

// Require CSS
require('normalize.css');

const theme = createTheme(
  {
    primary: 'white',
    secondary: '#1F2022',
    tertiary: '#03A9FC',
    quartenary: '#CECECE',
  },
  {
    primary: 'Montserrat',
    secondary: 'Helvetica',
  }
);

export default class Presentation extends React.Component {
  render() {
    return (
      <Deck
        transition={['zoom', 'slide']}
        transitionDuration={500}
        theme={theme}
      >
        <Slide transition={['zoom']} bgColor="primary">
          {/* WHITE BG */}
          <Heading size={1} fit caps lineHeight={1} textColor="secondary">
            Building CLI Tools with Node
          </Heading>
          <Heading size={6} caps lineHeight={3} textColor="tertiary">
            By Will Farley
          </Heading>
          <Text textColor="tertiary">@wfarley</Text>
          <br />
          <Text textColor="secondary">goldhand.github.io/presents-node-cli-tools</Text>
        </Slide>
        <Slide transition={['fade']} bgColor="tertiary">
          {/* BLUE BG */}
          <Heading size={6} textColor="primary" caps>
            Create a new node module
          </Heading>
          <Text size={6} textColor="secondary">
            <Code>$ npm init --yes</Code>
          </Text>
          <Text size={6} textColor="secondary">
            This creates a <Code>package.json</Code> with all the default options.
          </Text>
        </Slide>
        <Slide transition={['fade']} bgColor="primary" textColor="tertiary">
          {/* WHITE BG */}
          <Heading size={6} textColor="secondary" caps>
            Create a file for your cli script
          </Heading>
          <List>
            <ListItem>Convention: <Code>cli.js</Code></ListItem>
            <ListItem><Code>$ chmod +x cli.js</Code></ListItem>
            <ListItem>Add interpreter directive (shebang line):</ListItem>
            <ListItem><Code>#!/usr/bin/env node</Code></ListItem>
          </List>
        </Slide>
        <Slide transition={['fade']} bgColor="secondary" textColor="primary">
          {/* BLACK BG */}
          <Heading size={6} textColor="tertiary" caps>
            Tell node where to find this file
          </Heading>
          <Text>
            <Code textColor="primary">{'"bin": { "my-command": "./cli.js" }'}</Code>
          </Text>
        </Slide>
        <Slide transition={['zoom']} bgColor="primary">
          <Heading size={1} fit caps lineHeight={1} textColor="secondary">
            Create a global link
          </Heading>
          <Text textColor="tertiary">
            To test your cli during development, create a global alias to this module using:
          </Text>
          <Text size={6} textColor="tertiary">
            <Code>$ npm link</Code>
          </Text>
        </Slide>
        <Slide transition={['fade']} bgColor="tertiary">
          {/* BLUE BG */}
          <Heading size={6} textColor="primary" caps>
            Test out the cli tool
          </Heading>
          <List>
            <ListItem>Add <Code>console.log('Hello World');</Code> to <Code>cli.js</Code></ListItem>
            <ListItem>Run <Code>$ my-command</Code></ListItem>
            <ListItem><Code>> 'Hello World'</Code> should be printed.</ListItem>
          </List>
        </Slide>
        <Slide transition={['fade']} bgColor="primary" textColor="tertiary">
          {/* WHITE BG */}
          <Heading size={6} textColor="secondary" caps>
            Take some arguments
          </Heading>
          <CodePane lang="javascript" source={`
const [execPath, filePath, ...commandLineArgs] = process.argv;
console.log(JSON.stringify(commandLineArgs));`} />
          <CodePane source={`
$ my-command foo -b bar --baz=2
> ['foo', '-b', 'bar', '--baz=2']`}/>
        </Slide>
        <Slide transition={['fade']} bgColor="secondary" textColor="primary">
          {/* BLACK BG */}
          <Heading size={6} textColor="tertiary" caps>
            YARGS 🚣
          </Heading>
          <BlockQuote>
            <Quote textSize="1em">Yargs helps you build interactive command line tools, by parsing arguments and generating an elegant user interface.</Quote>
            <Cite>@github/yargs/yargs</Cite>
          </BlockQuote>
        </Slide>
        <Slide transition={['zoom']} bgColor="primary">
          <Heading size={1} fit caps lineHeight={1} textColor="secondary">
            But it's so much more...
          </Heading>
        </Slide>
        <Slide transition={['fade']} bgColor="tertiary">
          {/* BLUE BG */}
          <Heading size={6} textColor="primary" caps>
            From earlier...
          </Heading>
          <CodePane lang="javascript" source={`
const comandLineArgs = require('yargs').argv;
console.log(JSON.stringify(commandLineArgs));`} />
          <CodePane source={`
$ my-command foo -b bar --baz=2
> {_: ['foo'], b: 'bar', baz: 2}
            `}/>
        </Slide>
        <Slide transition={['fade']} bgColor="primary" textColor="tertiary">
          {/* WHITE BG */}
          <Heading size={6} textColor="secondary" caps>
            Create a nicer interface for users:
          </Heading>
          <List>
            <ListItem><Code>.help</Code></ListItem>
            <ListItem><Code>.usage</Code></ListItem>
            <ListItem><Code>.example</Code></ListItem>
          </List>
          <CodePane lang="javascript" source={`
require('yargs')
  .usage('Usage: $0 <command> [options]')
  .example('$0 release web')
  .argv;`} />
          <CodePane source={`
$ my-command --help
Usage: my-command <command> [options]

Examples:
  my-command release web
            `}/>
        </Slide>
        <Slide transition={['fade']} bgColor="secondary" textColor="primary">
          {/* BLACK BG */}
          <Heading size={6} textColor="tertiary" caps>
            Make your CLI configurable
          </Heading>
          <List>
            <ListItem><Code textColor="primary">.config</Code></ListItem>
            <ListItem><Code textColor="primary">.pkgConf</Code></ListItem>
          </List>
          <CodePane lang="javascript" source={`
require('yargs')
  .pkgConf('emc')
  .argv;`} />
          <CodePane lang="json" source={`
"emc": {
  "target": "web"
}`}/>
          <CodePane source={`
$ my-command foo
> {_: ['foo'], target: 'web'}`}/>
        </Slide>
        <Slide transition={['zoom']} bgColor="primary">
          <Heading size={1} fit caps lineHeight={1} textColor="secondary">
            Extend your CLI configuration
          </Heading>
          <CodePane lang="json" source={`
"emc": {
  "extends": "my-app/emc.js"
}`}/>
        </Slide>
        <Slide transition={['fade']} bgColor="tertiary">
          {/* BLUE BG */}
          <Heading size={6} textColor="primary" caps>
            Make yargs aware of options that could exist
          </Heading>
          <Text size={6} textColor="secondary">
            <Code>.option</Code>
          </Text>
        </Slide>
        <Slide transition={['fade']} bgColor="primary" textColor="tertiary">
          {/* WHITE BG */}
          <CodePane lang="javascript" source={`
require('yargs')
  .options({
    'run': {
      alias: 'r',
      describe: 'run your program',
      demandOption: true
    },
    'path': {
      alias: 'p',
      describe: 'provide a path to file',
      demandOption: true
    },
    'spec': {
      alias: 's',
      describe: 'program specifications'
    }
  })
  .argv;`}/>
          <CodePane source={`
$ my-command --help
Options:
  --run, -r   run your program                                       [required]
  --path, -p  provide a path to file                                 [required]
  --spec, -s  program specifications
  --help      Show help                                               [boolean]

Missing required arguments: run, path`}/>
        </Slide>
        <Slide transition={['fade']} bgColor="secondary" textColor="primary">
          {/* BLACK BG */}
          <Heading size={6} textColor="tertiary" caps>
            Define the commands that are exposed by your application
          </Heading>
          <Text size={6} textColor="primary">
            <Code textColor="primary">.command</Code>
          </Text>
        </Slide>
        <Slide transition={['zoom']} bgColor="primary">
          <Heading size={1} fit caps lineHeight={1} textColor="secondary">
          </Heading>
          <CodePane lang="javascript" source={`
require('yargs')
  .command(['start [app]', 'run', 'up'], 'Start up an app', {}, (argv) => {
    console.log('starting up the', argv.app || 'default', 'app')
  })
  .command({
    command: 'configure <key> [value]',
    aliases: ['config', 'cfg'],
    desc: 'Set a config variable',
    builder: (yargs) => yargs.default('value', 'true'),
    handler: (argv) => {
      console.log(\`setting \${argv.key} to \${argv.value}\`)
    }
  })
  .demandCommand()
  .argv;`}/>
          <CodePane source={`
$ my-command --help
Commands:
  start [app]              Start up an app            [aliases: run, up]
  configure <key> [value]  Set a config variable  [aliases: config, cfg]

Options:
  --help  Show help                                            [boolean]

$ my-command cfg concurrency 4
setting concurrency to 4

$ my-command run web
starting up the web app`}/>
        </Slide>
        <Slide transition={['fade']} bgColor="tertiary">
          {/* BLUE BG */}
          <Heading size={6} textColor="primary" caps>
            For specifics about the command:
          </Heading>
          <Text size={6} textColor="secondary">
            <Code>.builder</Code>
          </Text>
          <CodePane lang="javascript" source={`
require('yargs')
  .command({
    command: 'configure <key> [value]',
    builder: (yargs) => yargs.default('value', 'true'),
  })
  .argv;`}/>
        </Slide>
        <Slide transition={['fade']} bgColor="primary" textColor="tertiary">
          {/* WHITE BG */}
          <Heading size={6} textColor="secondary" caps>
            To execute a command:
          </Heading>
          <Text size={6} textColor="secondary">
            <Code>.handler</Code>
          </Text>
          <CodePane lang="javascript" source={`
require('yargs')
  .command({
    command: 'configure <key> [value]',
    handler: (argv) => {
      console.log(\`setting \${argv.key} to \${argv.value}\`)
    },
  })
  .argv;`}/>
        </Slide>
        <Slide transition={['fade']} bgColor="primary">
          {/* BLACK BG */}
          <Heading size={6} textColor="tertiary" caps>
            Yargs has an implied project structure with
          </Heading>
          <Text size={6} textColor="primary">
            <Code>.commandDir</Code>
            <br />
            <Link href="https://github.com/yargs/yargs/blob/master/docs/advanced.md#example-command-hierarchy-using-commanddir">
              commandDir() Example Structure
            </Link>
          </Text>
        </Slide>
        <Slide transition={['zoom']} bgColor="primary">
          <Heading size={1} fit caps lineHeight={1} textColor="secondary">
            Building CLI Tools with Node
          </Heading>
          <Heading size={6} caps lineHeight={3} textColor="secondary">
            By Will Farley
          </Heading>
          <Text textColor="tertiary">@wfarley</Text>
          <br />
          <Text textColor="secondary">goldhand.github.io/presents-node-cli-tools</Text>
        </Slide>
      </Deck>
    );
  }
}



// WEBPACK FOOTER //
// ./src/presentation.js